/**
 * Snapshot-freshness timeline for the camera side panel (Phase V.1 Part 4).
 *
 *   ● 3 mins ago  ────►  next ~3 mins
 *
 * The dot is blue while the next fetch is still in the future and turns amber
 * once the camera is past its expected refresh. The "next" half is omitted when
 * the source has no known refresh cadence (refresh_interval_s null), and the
 * whole thing degrades to "no fetch yet" before the first successful snapshot.
 *
 * Rendered only for snapshot cameras (live cams show a player instead).
 */

const BLUE = '#38bdf8';
const AMBER = '#fbbf24';

export type FreshnessKind = 'no-fetch' | 'ago-only' | 'upcoming' | 'overdue';

export interface Freshness {
  kind: FreshnessKind;
  /** "3 mins ago" / "just now" — null only for the no-fetch case. */
  agoLabel: string | null;
  /** "next ~3 mins" / "due now" / "4 mins overdue" — null when there's no
   *  cadence to project from, or before the first fetch. */
  rightLabel: string | null;
  /** Drives the dot colour: true → amber (overdue), false → blue. */
  amber: boolean;
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? '' : 's'}`;
}

/** Humanize a positive elapsed duration as "X <unit> ago". Sub-minute reads as
 *  "just now"; future timestamps (clock skew / broken sources) clamp to it too
 *  rather than emitting a negative "ago". Exported for the wall's honest media
 *  labels (#232) so panel + wall age strings come from one formatter. */
export function humanizeAgo(deltaMs: number): string {
  if (deltaMs < 45_000) return 'just now';
  const mins = Math.round(deltaMs / 60_000);
  if (mins < 60) return `${plural(mins, 'min')} ago`;
  const hours = Math.round(deltaMs / 3_600_000);
  if (hours < 24) return `${plural(hours, 'hour')} ago`;
  const days = Math.round(deltaMs / 86_400_000);
  return `${plural(days, 'day')} ago`;
}

/**
 * Pure freshness computation — no clock access, so tests pass `nowMs`
 * explicitly. Guards against null/unparseable/future `lastSnapshotAt` and a
 * missing/non-positive interval so it never emits NaN or negative durations.
 */
export function computeFreshness(
  lastSnapshotAt: string | null | undefined,
  refreshIntervalS: number | null | undefined,
  nowMs: number,
): Freshness {
  const lastMs = lastSnapshotAt ? Date.parse(lastSnapshotAt) : Number.NaN;
  if (!Number.isFinite(lastMs)) {
    return { kind: 'no-fetch', agoLabel: null, rightLabel: null, amber: false };
  }
  // Clamp future timestamps to "now" so the elapsed half stays sane.
  const elapsedMs = Math.max(0, nowMs - lastMs);
  const agoLabel = humanizeAgo(elapsedMs);

  const hasInterval = typeof refreshIntervalS === 'number' && refreshIntervalS > 0;
  if (!hasInterval) {
    return { kind: 'ago-only', agoLabel, rightLabel: null, amber: false };
  }

  const dueAtMs = lastMs + refreshIntervalS * 1000;
  if (nowMs < dueAtMs) {
    const mins = Math.round((dueAtMs - nowMs) / 60_000);
    const rightLabel = mins < 1 ? 'next <1 min' : `next ~${plural(mins, 'min')}`;
    return { kind: 'upcoming', agoLabel, rightLabel, amber: false };
  }
  const overMins = Math.floor((nowMs - dueAtMs) / 60_000);
  const rightLabel = overMins < 1 ? 'due now' : `${plural(overMins, 'min')} overdue`;
  return { kind: 'overdue', agoLabel, rightLabel, amber: true };
}

/** ISO timestamp → "YYYY-MM-DD HH:MM" (UTC), or null when unparseable. The
 *  absolute companion to the relative "X ago" so the operator can reconcile the
 *  dot against the time stamped into the photo itself (Stage 5 §5.4). */
function isoToAbsolute(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toISOString().slice(0, 16).replace('T', ' ');
}

export function FreshnessIndicator({
  capturedAt,
  lastSnapshotAt,
  proxiedAtMs,
  refreshIntervalS,
  now,
}: {
  /** Upstream photo-capture time when the source exposes one (Stage 5 §5.4).
   *  When present + parseable it is the primary timestamp, labelled "Captured". */
  capturedAt?: string | null;
  /** When we last ingested/proxied the image (DB last_snapshot_at). Used as the
   *  fallback timestamp, labelled honestly as "Ingested" — NOT photo time. */
  lastSnapshotAt: string | null | undefined;
  /** QA-3 (c): client wall-clock (ms) of the moment the snapshot the operator is
   *  looking at finished proxying THIS view. The snapshot proxy fetches a live
   *  frame on every view but only stamps last_snapshot_at server-side, so the
   *  cached detail payload can read days stale even though the image on screen is
   *  seconds old. When we have no real captured_at, this live load time is a far
   *  more honest "Ingested" age than the stale DB value. Null until the image
   *  loads, or when the frame isn't a fresh live proxy (broken / stale source). */
  proxiedAtMs?: number | null;
  refreshIntervalS: number | null | undefined;
  /** Injectable clock for deterministic rendering/tests; defaults to wall time. */
  now?: number;
}): JSX.Element {
  const nowMs = now ?? Date.now();
  // §5.4: the blue dot used to read DB last_snapshot_at (our ingestion time) but
  // was presented as if it were when the photo was taken — which mismatched the
  // timestamp burned into many source images. Prefer the real capture time when
  // we have it; otherwise fall back to ingestion time and say so ("Ingested").
  const capturedMs = capturedAt ? Date.parse(capturedAt) : Number.NaN;
  const usingCaptured = Number.isFinite(capturedMs);
  const hasProxiedAt = typeof proxiedAtMs === 'number' && Number.isFinite(proxiedAtMs);
  // Fallback timestamp (no real capture time): prefer the live proxy load time
  // over the stale cached last_snapshot_at so a just-fetched image never reads
  // "3 days ago" (QA-3 (c)). Still labelled "Ingested" — it's our fetch time,
  // not the photo's, just now reflecting reality instead of misleading.
  const fallbackIso = hasProxiedAt
    ? new Date(proxiedAtMs as number).toISOString()
    : (lastSnapshotAt ?? null);
  const primaryIso = usingCaptured ? capturedAt! : fallbackIso;
  const label = usingCaptured ? 'Captured' : 'Ingested';

  const f = computeFreshness(primaryIso, refreshIntervalS, nowMs);
  const dotColor = f.amber ? AMBER : BLUE;
  const absolute = f.kind === 'no-fetch' ? null : isoToAbsolute(primaryIso);
  const agoText = f.kind === 'no-fetch' ? 'no fetch yet' : f.agoLabel;

  return (
    <div
      data-testid="freshness-indicator"
      className="flex items-center gap-2 text-xs text-slate-400"
    >
      <span
        data-testid="freshness-dot"
        className="inline-block rounded-full shrink-0"
        style={{ width: 8, height: 8, backgroundColor: dotColor }}
      />
      {f.kind !== 'no-fetch' && (
        <span data-testid="freshness-label" className="shrink-0 font-medium text-slate-300">
          {label}:
        </span>
      )}
      {absolute && (
        <span data-testid="freshness-absolute" className="shrink-0 font-mono">
          {absolute}
        </span>
      )}
      <span className="shrink-0">{f.kind === 'no-fetch' ? agoText : `(${agoText})`}</span>
      {f.rightLabel && (
        <>
          <span className="flex-1 border-t border-dashed border-slate-600" aria-hidden="true" />
          <span className="shrink-0 inline-flex items-center gap-1">
            <span aria-hidden="true">▸</span>
            {f.rightLabel}
          </span>
        </>
      )}
    </div>
  );
}
